import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

// components
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { PdpSharedSellerAssociations } from './PdpSharedSellerAssociations.tsx';
import { getIsNewSeller } from '../../../utils/sellerHelpers.ts';

// styles
import styles from './PdpSharedSellerInfo.scss';

// types
import { type PdpSharedSellerInfo_viewer$key } from './__generated__/PdpSharedSellerInfo_viewer.graphql.ts';
import { type PdpSharedSellerInfo_seller$key as Seller } from './__generated__/PdpSharedSellerInfo_seller.graphql.ts';

const MINIMUM_ORDER_QUANTITY = 5;

const formatResponseTime = (
    responseTime: number | null | undefined,
    displayZeroResponseTime: boolean
): ReactNode => {
    if (responseTime === null || responseTime === undefined || isNaN(responseTime)) {
        return null;
    }

    if (responseTime === 0 && !displayZeroResponseTime) {
        return null;
    }

    if (responseTime > 168) {
        return (
            <FormattedMessage id="PdpSharedSellerInfo.responseTimeWeek" defaultMessage="A week" />
        );
    }

    if (responseTime > 48) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeSeveralDays"
                defaultMessage="Several days"
            />
        );
    }

    if (responseTime > 23) {
        return (
            <FormattedMessage
                id="PdpSharedSellerInfo.responseTimeFewDays"
                defaultMessage="1 to 2 days"
            />
        );
    }

    return (
        <FormattedMessage
            id="PdpSharedSellerInfo.responseTimeValue"
            defaultMessage="{responseTime, select,
              0 {<1 hour}
              1 {1 hour}
              other {{responseTime} hours}
            }"
            values={{ responseTime }}
        />
    );
};

const viewerFragment = graphql`
    fragment PdpSharedSellerInfo_viewer on Viewer {
        suppressNewSellerSignals: featureFlag(feature: "suppressNewSellerSignals")
    }
`;

const sellerFragment = graphql`
    fragment PdpSharedSellerInfo_seller on Seller {
        ...PdpSharedSellerAssociations_seller
        agreementStart
        sellerOrderQuantity
        analytics {
            metrics {
                responseTime {
                    value
                }
            }
        }
        liveStorefrontProfile {
            establishmentYear
        }
        shippingAddress {
            displayFullCityStateCountry: displayCityStateCountry(fullCountryName: true)
        }
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
    }
`;

export const PdpSharedSellerInfo: FC<{
    viewer: PdpSharedSellerInfo_viewer$key | null;
    seller: Seller;
    showLocation?: boolean;
    showAssociations?: boolean;
    showOrderQuantity?: boolean;
    showOneLineEstablishment?: boolean;
    orderQuantityLocation?: 'top' | 'bottom' | 'none';
    displayZeroResponseTime?: boolean;
    textSize?: 'small' | 'default';
}> = ({
    viewer: viewerRef,
    seller: sellerRef,
    showLocation = false,
    showAssociations = true,
    orderQuantityLocation = 'bottom',
    showOneLineEstablishment = false,
    displayZeroResponseTime = false,
    textSize = 'default',
}) => {
    const { formatNumber } = useIntl();
    const viewer = useFragment(viewerFragment, viewerRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const {
        analytics,
        agreementStart,
        sellerOrderQuantity,
        liveStorefrontProfile,
        monthsSinceStartDate,
    } = seller;
    const establishmentYear = liveStorefrontProfile?.establishmentYear;
    const dealerSince = agreementStart ? new Date(agreementStart).getUTCFullYear() : null;
    const isNewSeller = getIsNewSeller(monthsSinceStartDate);
    const showDealerSince = dealerSince && (!isNewSeller || !viewer?.suppressNewSellerSignals);
    const responseTime = formatResponseTime(
        analytics?.metrics?.responseTime?.value,
        displayZeroResponseTime
    );

    const sellerLocation = seller?.shippingAddress?.displayFullCityStateCountry;

    const establishmentYearEl = establishmentYear && (
        <span
            className={classnames(styles.info, {
                [styles.small]: textSize === 'small',
            })}
            data-tn="dealer-establishmentYear"
        >
            <FormattedMessage
                id="PdpSharedSellerInfo.establishmentYear"
                defaultMessage="Established in {establishmentYear}"
                values={{ establishmentYear }}
            />
        </span>
    );

    const orderQuantityEl = sellerOrderQuantity &&
        sellerOrderQuantity >= MINIMUM_ORDER_QUANTITY && (
            <div
                className={classnames(styles.info, { [styles.small]: textSize === 'small' })}
                data-tn="dealer-orderQuantity"
            >
                <FormattedMessage
                    id="PdpSharedSellerInfo.sellerOrderQuantity"
                    defaultMessage="{sellerOrderQuantity} sales on 1stDibs"
                    values={{ sellerOrderQuantity: formatNumber(sellerOrderQuantity) }}
                />
            </div>
        );

    return (
        <>
            {orderQuantityLocation === 'top' && orderQuantityEl}
            {showLocation && sellerLocation && (
                <div
                    className={classnames(styles.info, {
                        [styles.small]: textSize === 'small',
                    })}
                >
                    <FormattedMessage
                        id="PdpSharedSellerInfo.location"
                        defaultMessage="Located in {sellerLocation}"
                        values={{ sellerLocation }}
                    />
                </div>
            )}
            {showOneLineEstablishment ? (
                (dealerSince || establishmentYear) && (
                    <div
                        className={classnames(styles.info, {
                            [styles.small]: textSize === 'small',
                        })}
                    >
                        {showDealerSince && (
                            <span data-tn="dealer-since">
                                <FormattedMessage
                                    id="PdpSharedSellerInfo.since"
                                    defaultMessage="1stDibs seller since {dealerSince}"
                                    values={{ dealerSince }}
                                />
                            </span>
                        )}
                        {showDealerSince && establishmentYear && <span>, </span>}
                        {establishmentYear && establishmentYearEl}
                    </div>
                )
            ) : (
                <>
                    {establishmentYearEl && <div>{establishmentYearEl}</div>}
                    {showDealerSince && (
                        <div
                            className={classnames(styles.info, {
                                [styles.small]: textSize === 'small',
                            })}
                            data-tn="dealer-since"
                        >
                            <FormattedMessage
                                id="PdpSharedSellerInfo.since"
                                defaultMessage="1stDibs seller since {dealerSince}"
                                values={{ dealerSince }}
                            />
                        </div>
                    )}
                </>
            )}
            {orderQuantityLocation === 'bottom' && orderQuantityEl}

            {responseTime && (
                <div
                    className={classnames(styles.info, {
                        [styles.small]: textSize === 'small',
                    })}
                    data-tn="dealer-response-time"
                >
                    <FormattedMessage
                        id="PdpSharedSellerInfo.response_time"
                        defaultMessage="Typical response time: {responseTime}"
                        values={{ responseTime }}
                    />
                </div>
            )}
            {showAssociations && <PdpSharedSellerAssociations seller={seller} />}
        </>
    );
};
